import { useContext, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "store/store";
import { MediaContext } from "context/MediaContext";
import { SessionScope } from "globals/enums";
import { noiseAlertHandler } from "helpers/noise-alert-helper";

const useNoiseDetection = () => {
  const { audioStream } = useContext(MediaContext);
  const dispatch = useAppDispatch();
  const { session, twilioAudioCallStatus, precheckSuccess, alertConfigs, configuration } =
    useAppSelector((state) => state.app);
  const isNoiseDetectionDisabled = configuration.disable_noise_detection;
  const isAudioCheckEnabled = configuration.audio_check;
  const isCandidateAuth = session.session_type === SessionScope.CandidateAuth;
  const processor = useRef(null);
  const audioCounter = useRef(0);
  const audioContext = useRef(null);
  const noiseAlertTimer = useRef<NodeJS.Timer | null>(null);

  const startNoiseDetection = () => {
    audioContext.current = new AudioContext();
    if (audioStream) {
      const streamNode = audioContext.current.createMediaStreamSource(audioStream);
      const meter = createAudioMeter(audioContext.current);
      streamNode.connect(meter);
    }
  };

  const calculateRMS = (buffer) => {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      if (Math.abs(buffer[i]) >= processor.current.clipLevel) {
        processor.current.clipping = true;
        processor.current.lastClip = window.performance.now();
      }
      sum += buffer[i] * buffer[i];
    }
    const meanSquare = sum / buffer.length;
    const rms = Math.sqrt(meanSquare);
    return rms;
  };

  const createAudioMeter = (audioContext: any) => {
    processor.current = audioContext.createScriptProcessor(512);
    processor.current.onaudioprocess = (audioProcessingEvent: any) => {
      const buf = audioProcessingEvent.inputBuffer.getChannelData(0);
      const rms = calculateRMS(buf);
      processor.current.volume = Math.max(
        rms,
        processor.current.volume * processor.current.averaging
      );
      if (processor.current.volume > 0.02) {
        const audioLevel = processor.current.volume * 400;
        if (audioLevel > 40) {
          audioCounter.current++;
        }
      }
    };
    processor.current.clipping = false;
    processor.current.lastClip = 0;
    processor.current.volume = 0;
    processor.current.clipLevel = 0.98;
    processor.current.averaging = 0.95;
    processor.current.clipLag = 750;
    processor.current.connect(audioContext.destination);
    processor.current.checkClipping = () => {
      if (!processor.current.clipping) {
        return false;
      }
      if (processor.current.lastClip + processor.current.clipLag < window.performance.now()) {
        processor.current.clipping = false;
      }
      return processor.current.clipping;
    };

    processor.current.shutdown = () => {
      processor.current.disconnect();
      processor.current.onaudioprocess = null;
    };
    return processor.current;
  };

  useEffect(() => {
    noiseAlertTimer.current = setInterval(() => {
      noiseAlertHandler(processor, audioCounter, twilioAudioCallStatus, alertConfigs, dispatch);
    }, 1000);
    return () => clearInterval(noiseAlertTimer.current as any);
  }, []);

  useEffect(() => {
    const startDetection =
      audioStream &&
      isAudioCheckEnabled &&
      !isCandidateAuth &&
      !processor.current &&
      precheckSuccess &&
      !isNoiseDetectionDisabled;

    const stopDetection = !audioStream && processor.current;

    if (startDetection) {
      startNoiseDetection();
    }

    if (stopDetection) {
      processor.current.shutdown();
      clearInterval(noiseAlertTimer.current as any);
    }

    return () => {
      processor.current?.shutdown();
    };
  }, [
    audioStream,
    isCandidateAuth,
    precheckSuccess,
    isNoiseDetectionDisabled,
    isAudioCheckEnabled
  ]);

  return;
};

export default useNoiseDetection;
